import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { availableAuditEntriesAtom } from '@/global-store';
import { useFetchAuditEntriesByIds } from '@/http/queries/useFetchAuditEntriesLookup';
import type { AuditData } from '@eye-able/types';

/**
 * Admins receive a bounded window of report licenses (recent + pinned), so an
 * id referenced by a todo, export job, test, or PDF may point at a domain
 * outside that window. Resolves any such ids via a direct server lookup and
 * merges them into the licensed list, so callers can treat the result as
 * complete regardless of role.
 */
export function useAuditEntriesWithFallback(
  referencedIds: Array<number | string | null | undefined>,
): { auditEntries: AuditData[]; isLoading: boolean } {
  const availableAuditEntries = useAtomValue(availableAuditEntriesAtom);

  const missingIds = useMemo(() => {
    const knownIds = new Set(availableAuditEntries.map((entry) => entry.id));

    return Array.from(
      new Set(
        referencedIds
          .map((id) => (id == null ? null : Number(id)))
          .filter(
            (id): id is number =>
              id != null && !Number.isNaN(id) && !knownIds.has(id),
          ),
      ),
    );
  }, [availableAuditEntries, referencedIds]);

  const { data: resolvedEntries, isLoading } =
    useFetchAuditEntriesByIds(missingIds);

  const auditEntries = useMemo(() => {
    if (!resolvedEntries?.length) return availableAuditEntries;

    const knownIds = new Set(availableAuditEntries.map((entry) => entry.id));

    return [
      ...availableAuditEntries,
      ...resolvedEntries.filter((entry) => !knownIds.has(entry.id)),
    ];
  }, [availableAuditEntries, resolvedEntries]);

  return { auditEntries, isLoading };
}
